<script lang="ts">
  /**
   * Push-varsler på denne enheten, under tannhjulet øverst. Av til brukeren slår
   * dem på, og brukeren velger hva det varsles om. Se lib/pushvarsler.ts.
   */
  import { onMount } from 'svelte';
  import {
    VARSELVALG, STANDARDVALG, pushStotte, hentAbonnement, slaaPaa, slaaAv, lagreValg, sendTest,
    type Abonnement, type VarselType,
  } from '../lib/pushvarsler';
  import { visFeil, visOk } from '../lib/toast';

  const stotte = pushStotte();
  let abonnement = $state<Abonnement | null>(null);
  let laster = $state(true);
  let jobber = $state(false);

  onMount(async () => {
    try {
      abonnement = await hentAbonnement();
    } catch {
      abonnement = null;
    }
    laster = false;
  });

  async function kjor(fn: () => Promise<void>) {
    if (jobber) return;
    jobber = true;
    try {
      await fn();
    } catch (e) {
      visFeil(e instanceof Error ? e.message : 'Noe gikk galt. Prøv igjen.');
    }
    jobber = false;
  }

  function veksle() {
    kjor(async () => {
      if (abonnement) {
        await slaaAv(abonnement.endpoint);
        abonnement = null;
        visOk('Varslene er slått av på denne enheten.');
      } else {
        abonnement = await slaaPaa([...STANDARDVALG]);
        visOk('Varslene er på. Trykk «Send testvarsel» for å sjekke.');
      }
    });
  }

  function velg(type: VarselType) {
    const a = abonnement;
    if (!a) return;
    const valg = a.valg.includes(type) ? a.valg.filter((t) => t !== type) : [...a.valg, type];
    kjor(async () => {
      await lagreValg(a.endpoint, valg);
      abonnement = { ...a, valg };
    });
  }

  function test() {
    const a = abonnement;
    if (!a) return;
    kjor(async () => {
      await sendTest(a.endpoint);
      visOk('Testvarselet er sendt.');
    });
  }
</script>

<section>
  <div class="flex items-center gap-3 mb-1.5">
    <h3 class="label">Varsler</h3>
    {#if stotte === 'ok' && !laster}
      <button
        class="ml-auto w-11 h-[25px] rounded-full p-[3px] flex items-center transition-all duration-200 disabled:opacity-50 {abonnement
          ? 'bg-leaf justify-end'
          : 'bg-border justify-start'}"
        onclick={veksle}
        disabled={jobber}
        role="switch"
        aria-checked={abonnement !== null}
        aria-label="Varsler på denne enheten"
      >
        <span class="w-[19px] h-[19px] rounded-full bg-bg block shadow"></span>
      </button>
    {/if}
  </div>

  {#if stotte === 'hjemskjerm'}
    <p class="text-[12px] text-text-muted leading-snug">
      På iPhone virker varsler bare når appen er lagt til på hjemskjermen: Del → Legg til på Hjem-skjerm.
      Åpne den derfra og slå dem på her.
    </p>
  {:else if stotte === 'nei'}
    <p class="text-[12px] text-text-muted leading-snug">Denne nettleseren støtter ikke varsler.</p>
  {:else if !abonnement}
    <p class="text-[12px] text-text-muted leading-snug">
      Av. Slår du dem på, får denne enheten beskjed når noe trenger deg. Du velger selv hva.
    </p>
  {:else}
    <p class="text-[12px] text-text-muted leading-snug mb-2">Varsle meg på denne enheten om:</p>
    <div class="flex flex-col gap-1.5">
      {#each VARSELVALG as v (v.type)}
        <button
          class="text-xs px-2.5 py-2 rounded-lg border text-left transition-colors {abonnement.valg.includes(v.type)
            ? 'bg-leaf/10 text-leaf-glow border-leaf/30'
            : 'bg-surface-raised text-text-muted border-border'}"
          onclick={() => velg(v.type)}
          disabled={jobber}
          aria-pressed={abonnement.valg.includes(v.type)}
        >
          <span class="block">{v.navn}</span>
          <span class="block text-[11px] text-text-dim">{v.forklaring}</span>
        </button>
      {/each}
    </div>
    <button class="btn-secondary !py-2 w-full mt-2" onclick={test} disabled={jobber}>Send testvarsel</button>
  {/if}
</section>
